/**
 * Tenant-wide Agent Actions (Activity) API service.
 *
 * Reads the connector-agnostic log of everything the agents did — tickets filed
 * in HubSpot/Jira, escalations, leads, meetings, requirement briefs, and future
 * MCP tools — newest first. Tenant comes from the `x-tenant-id` header (axios
 * interceptor). Pure async functions; no React state.
 */
import { get } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import type { AgentActionEntry, SessionOutcomeKind } from '@features/observability/types';

export interface ListAgentActionsQuery {
  agentId?: string | null;
  /** Exact tool name (maps 1:1 to an action "type" in the UI). */
  toolName?: string | null;
  status?: string | null;
  from?: string | null;
  to?: string | null;
  page?: number;
  limit?: number;
}

export interface AgentActionsResult {
  items: AgentActionEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface AgentActionGroup {
  sessionId: string;
  agentId: string;
  startedAt: string;
  lastActionAt: string;
  actionCount: number;
  outcome?: SessionOutcomeKind;
  escalated?: boolean;
  actions: AgentActionEntry[];
}

export interface AgentActionGroupsResult {
  items: AgentActionGroup[];
  total: number;
  page: number;
  limit: number;
}

export const agentActionsService = {
  list(query: ListAgentActionsQuery = {}): Promise<AgentActionsResult> {
    const params: Record<string, string> = {};
    if (query.agentId) params.agentId = query.agentId;
    if (query.toolName) params.toolName = query.toolName;
    if (query.status) params.status = query.status;
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.page) params.page = String(query.page);
    if (query.limit) params.limit = String(query.limit);
    return get<AgentActionsResult>(endpoints.actions.list, { params });
  },

  listGrouped(query: ListAgentActionsQuery = {}): Promise<AgentActionGroupsResult> {
    const params: Record<string, string> = {};
    if (query.agentId) params.agentId = query.agentId;
    if (query.toolName) params.toolName = query.toolName;
    if (query.status) params.status = query.status;
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.page) params.page = String(query.page);
    if (query.limit) params.limit = String(query.limit);
    return get<AgentActionGroupsResult>(endpoints.actions.grouped, { params });
  },
} as const;
